import { AlertTriangle, Check, Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import type { IntegrationVendor } from "../api";
import { guessInvoiceNumber, guessVendor } from "../invoiceGuess";

export interface BulkRow {
  file: File;
  vendorId: string;
  invoiceNumber: string;
  invoiceDate: string;
  serviceStart: string;
  serviceEnd: string;
  subtotal: string;
  tax: string;
  total: string;
  quantity: string;
  status: "pending" | "saving" | "done" | "error";
  message?: string;
}

export function IntegrationBulkUploadDialog({
  open,
  onOpenChange,
  vendors,
  files,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  vendors: IntegrationVendor[];
  files: File[];
  /** Runs one row. Resolves on success, throws with a message on failure. */
  onSubmit: (row: BulkRow) => Promise<void>;
}) {
  const [rows, setRows] = useState<BulkRow[]>([]);
  const [running, setRunning] = useState(false);

  // Seed one row per file whenever a new batch arrives. useEffect, not
  // useMemo: seeding is a state update, and doing it during render crashes the
  // tree ("cannot update while rendering").
  useEffect(() => {
    setRows(
      files.map((file) => ({
        file,
        vendorId: guessVendor(file.name, vendors),
        invoiceNumber: guessInvoiceNumber(file.name),
        invoiceDate: "",
        serviceStart: "",
        serviceEnd: "",
        subtotal: "",
        tax: "",
        total: "",
        quantity: "",
        status: "pending" as const,
      })),
    );
  }, [files, vendors]);

  const patch = (i: number, next: Partial<BulkRow>) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...next } : r)));

  const ready = rows.filter(
    (r) => r.vendorId && r.invoiceNumber && r.status !== "done",
  );
  const done = rows.filter((r) => r.status === "done").length;
  const failed = rows.filter((r) => r.status === "error").length;

  async function saveAll() {
    setRunning(true);
    // Sequential on purpose: each row is a create + a line import, and a vendor
    // with two files (Vehlo bills Protractor and Tire Guru on one invoice
    // number) would race on the period+vendor+number unique constraint.
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (!row || !row.vendorId || !row.invoiceNumber || row.status === "done") continue;
      patch(i, { status: "saving", message: undefined });
      try {
        await onSubmit(row);
        patch(i, { status: "done", message: undefined });
      } catch (err) {
        patch(i, {
          status: "error",
          message: err instanceof Error ? err.message : "Failed",
        });
      }
    }
    setRunning(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Record {files.length} invoice{files.length === 1 ? "" : "s"}</DialogTitle>
          <DialogDescription>
            Each file needs a vendor and the totals off the invoice. The vendor and
            invoice number are guessed from the filename — check them. The{" "}
            <strong>service period</strong> is the month the invoice covers, which is
            often not its invoice date.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[55vh] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-background">
              <tr className="border-b text-left text-muted-foreground">
                <th className="py-2 pr-2 font-medium">File</th>
                <th className="py-2 pr-2 font-medium">Vendor</th>
                <th className="py-2 pr-2 font-medium">Invoice #</th>
                <th className="py-2 pr-2 font-medium">Service start</th>
                <th className="py-2 pr-2 font-medium">Service end</th>
                <th className="py-2 pr-2 font-medium">Subtotal</th>
                <th className="py-2 pr-2 font-medium">Total</th>
                <th className="py-2 pr-2 font-medium">Qty</th>
                <th className="py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.file.name} className="border-b last:border-0">
                  <td className="max-w-[13rem] truncate py-1.5 pr-2" title={row.file.name}>
                    {row.file.name}
                  </td>
                  <td className="py-1.5 pr-2">
                    <select
                      aria-label={`Vendor for ${row.file.name}`}
                      className={cn(
                        "h-8 w-40 rounded border bg-background px-1.5",
                        !row.vendorId && "border-warning",
                      )}
                      value={row.vendorId}
                      onChange={(e) => patch(i, { vendorId: e.target.value })}
                    >
                      <option value="">Select…</option>
                      {vendors.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-1.5 pr-2">
                    <Input
                      aria-label={`Invoice number for ${row.file.name}`}
                      className="h-8 w-32"
                      value={row.invoiceNumber}
                      onChange={(e) => patch(i, { invoiceNumber: e.target.value })}
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <Input
                      type="date"
                      aria-label={`Service start for ${row.file.name}`}
                      className="h-8 w-36"
                      value={row.serviceStart}
                      onChange={(e) => patch(i, { serviceStart: e.target.value })}
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <Input
                      type="date"
                      aria-label={`Service end for ${row.file.name}`}
                      className="h-8 w-36"
                      value={row.serviceEnd}
                      onChange={(e) => patch(i, { serviceEnd: e.target.value })}
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <Input
                      aria-label={`Subtotal for ${row.file.name}`}
                      className="h-8 w-24"
                      value={row.subtotal}
                      onChange={(e) => patch(i, { subtotal: e.target.value })}
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <Input
                      aria-label={`Total for ${row.file.name}`}
                      className="h-8 w-24"
                      value={row.total}
                      onChange={(e) => patch(i, { total: e.target.value })}
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <Input
                      aria-label={`Quantity for ${row.file.name}`}
                      className="h-8 w-16"
                      value={row.quantity}
                      onChange={(e) => patch(i, { quantity: e.target.value })}
                    />
                  </td>
                  <td className="py-1.5">
                    {row.status === "saving" && (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                    {row.status === "done" && <Check className="h-4 w-4 text-success" />}
                    {row.status === "error" && (
                      <span
                        className="inline-flex items-center gap-1 text-danger"
                        title={row.message}
                      >
                        <AlertTriangle className="h-4 w-4" />
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {failed > 0 && (
          <div className="rounded border border-danger/40 bg-danger-light px-3 py-2 text-xs text-danger">
            {failed} failed. Rows that saved are kept — fix the rest and run again;
            the ones already recorded are skipped.
            <ul className="mt-1 list-disc pl-4">
              {rows
                .filter((r) => r.status === "error")
                .map((r) => (
                  <li key={r.file.name}>
                    <strong>{r.file.name}</strong> — {r.message}
                  </li>
                ))}
            </ul>
          </div>
        )}

        <DialogFooter className="items-center">
          <span className="mr-auto text-xs text-muted-foreground">
            {done > 0 && `${done} recorded · `}
            {ready.length} ready
            {rows.some((r) => !r.vendorId) && " · some files need a vendor"}
          </span>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={running}>
            <X className="mr-1.5 h-3.5 w-3.5" />
            Close
          </Button>
          <Button onClick={saveAll} disabled={running || ready.length === 0}>
            {running && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Record {ready.length} invoice{ready.length === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
